import React from 'react';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { TreeNodeData } from './treeUtils';

/** D&D 移動のためのハンドラ群（editable が false のときは無効） */
export type TreeDnd = {
  /** D&D 移動が有効か（編集可能時のみ） */
  enabled: boolean;
  /** ドラッグ中のノード id（ドロップ可否判定・視覚表示用） */
  draggingId: string | null;
  /** 現在ドロップ先ハイライト中のフォルダ id */
  dropTargetId: string | null;
  /** ノードがドロップ先として受け入れ可能か（フォルダ / 循環でない 等） */
  canDropOn: (targetFolderId: string) => boolean;
  onDragStartNode: (id: string) => void;
  onDragEndNode: () => void;
  onDragOverFolder: (folderId: string) => void;
  onDropOnFolder: (folderId: string) => void;
};

type Props = {
  node: TreeNodeData;
  depth: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  /** 記事として公開済みのファイル fileId 集合（公開済みバッジ表示用） */
  articleFileIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (node: TreeNodeData) => void;
  onOpenFile: (node: TreeNodeData) => void;
  onContextMenu: (e: React.MouseEvent, node: TreeNodeData) => void;
  /** D&D 移動ハンドラ群 */
  dnd: TreeDnd;
};

/**
 * ツリーの1行（フォルダ/ファイル）。フォルダは開閉、ファイルは選択で開く。
 * 再帰的に子ノードを描画する。
 */
const TreeNode: React.FC<Props> = ({
  node,
  depth,
  selectedId,
  expandedIds,
  articleFileIds,
  onToggle,
  onSelect,
  onOpenFile,
  onContextMenu,
  dnd,
}) => {
  const isFolder = node.type === 'FOLDER';
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const isArticle = !isFolder && !!node.fileId && articleFileIds.has(node.fileId);
  // このフォルダがドロップ受け入れ中（ハイライト表示）か
  const isDropTarget = isFolder && dnd.dropTargetId === node.id;

  const handleClick = () => {
    onSelect(node);
    if (isFolder) {
      onToggle(node.id);
    } else {
      onOpenFile(node);
    }
  };

  // フォルダへのドロップ: 受け入れ可能なときだけ preventDefault してハイライトする
  const handleDragOver = (e: React.DragEvent) => {
    if (!dnd.enabled || !isFolder) return;
    if (!dnd.canDropOn(node.id)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dnd.onDragOverFolder(node.id);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!dnd.enabled || !isFolder) return;
    if (!dnd.canDropOn(node.id)) return;
    e.preventDefault();
    e.stopPropagation();
    dnd.onDropOnFolder(node.id);
  };

  return (
    <>
      <Box
        draggable={dnd.enabled}
        onDragStart={(e) => {
          if (!dnd.enabled) return;
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          // Firefox 等でドラッグを開始させるためにデータをセットする
          e.dataTransfer.setData('text/plain', node.id);
          dnd.onDragStartNode(node.id);
        }}
        onDragEnd={() => dnd.enabled && dnd.onDragEndNode()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        onContextMenu={(e) => onContextMenu(e, node)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pr: 1,
          pl: depth * 1.5 + 0.5,
          py: 0.4,
          // 内容幅で表示しつつ、パネル幅より狭い場合はハイライトを全幅に広げる
          minWidth: '100%',
          width: 'max-content',
          cursor: 'pointer',
          borderRadius: 1,
          userSelect: 'none',
          bgcolor: isDropTarget
            ? 'action.selected'
            : isSelected
              ? 'action.selected'
              : 'transparent',
          // ドロップ先フォルダは点線ボーダーで受け入れ可能を示す
          outline: isDropTarget ? '1px dashed' : 'none',
          outlineColor: 'primary.main',
          opacity: dnd.draggingId === node.id ? 0.5 : 1,
          transition: 'background-color 0.2s',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {isFolder ? (
          isExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )
        ) : (
          <Box sx={{ width: 18 }} />
        )}
        {isFolder ? (
          isExpanded ? (
            <FolderOpenIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          ) : (
            <FolderIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          )
        ) : (
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
        )}
        <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
          {node.name}
        </Typography>
        {isArticle && (
          <Tooltip title="記事として公開中">
            <ArticleOutlinedIcon sx={{ fontSize: 15, color: 'primary.main', ml: 0.25 }} />
          </Tooltip>
        )}
      </Box>
      {isFolder &&
        isExpanded &&
        node.children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            expandedIds={expandedIds}
            articleFileIds={articleFileIds}
            onToggle={onToggle}
            onSelect={onSelect}
            onOpenFile={onOpenFile}
            onContextMenu={onContextMenu}
            dnd={dnd}
          />
        ))}
    </>
  );
};

export default TreeNode;
